/**
 * User-submitted text (condolence names, messages, tribute names) is stored RAW.
 *
 * It used to be HTML-escaped on write by a `customEscape` helper, which caused two
 * bugs: it escaped `&` *after* `<`/`>`, double-encoding them (`<` -> `&lt;` ->
 * `&amp;lt;`), and it was applied to `name`, which React renders as a text node and
 * already escapes - so the entity showed up verbatim on the page ("Alex &amp; Babsi").
 *
 * Escaping now happens at render time, which is the only place that knows the output
 * context. Render user text through the `UserText` component (or `decodeUserText`)
 * so it lands in a text node - never in `dangerouslySetInnerHTML`.
 */

const ENTITIES: Record<string, string> = {
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&nbsp;": " ",
};

/**
 * Normalises a stored value for display.
 *
 * Handles both shapes we have in the database:
 *  - legacy: HTML-ish, with `<br/>` for newlines and (possibly double-encoded) entities
 *  - current: exactly what the visitor typed
 *
 * Decoding runs repeatedly because legacy values can be double-encoded: `&amp;lt;`
 * needs one pass to become `&lt;` and a second to become `<`. Output is always
 * plain text destined for a text node, so decoding cannot introduce markup.
 */
export const decodeUserText = (input?: string | null): string => {
  if (!input) return "";

  let out = input.replace(/<br\s*\/?>/gi, "\n");

  for (let pass = 0; pass < 3; pass++) {
    const next = out
      .replace(
        /&(lt|gt|quot|apos|nbsp|#39);/gi,
        (match) => ENTITIES[match.toLowerCase()] ?? match
      )
      .replace(/&amp;/gi, "&");
    if (next === out) break;
    out = next;
  }

  return out;
};
